import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import {
  Home,
  MessageSquare,
  FileText,
  X,
  ChevronLeft,
  ChevronRight,
  Plus,
  LucideIcon,
  Search,
  UserRoundPlus,
  BadgeInfo,
  BarChart3,
  ClipboardCheck,
  ListChecks,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ChevronsDown,
  ChevronsUp,
  MessageCircleHeart,
  Rocket,
} from 'lucide-react';
import logo from '../../assets/figma/ISentryxLogo.png';
import { useMenuLabels, type MenuLabels } from '../../hooks/useMenuLabels';
import { useAppSelector } from '../../store';

interface SidebarItem {
  name: string;
  labelId: keyof MenuLabels;
  href: string;
  icon: LucideIcon;
  badge?: string;
}

interface SidebarSection {
  title: string;
  labelId: keyof MenuLabels;
  hidden?: boolean;
  items: SidebarItem[];
}

const dashboardItem: SidebarItem = { name: 'Dashboard', labelId: 'dashboard', href: '/dashboard', icon: Home };

const sidebarSections: SidebarSection[] = [
  {
    title: 'MY NETWORK',
    labelId: 'myNetworkSection',
    hidden: false,
    items: [
      { name: 'Explore People', labelId: 'explorePeople', href: '/findPeople', icon: Search },
      { name: 'Talent & Expert Finder', labelId: 'askIsentryxAi', href: '/ask-isentryx-ai', icon: Sparkles },
      // { name: 'My Network', labelId: 'connections', href: '/MatchProfile', icon: Network },
    ],
  },
  {
    title: 'COLLABORATION',
    labelId: 'collaborationSection',
    items: [
      { name: 'Messages', labelId: 'messages', href: '/OverViewMessages', icon: MessageSquare },
      { name: 'List', labelId: 'list', href: '/groups', icon: UserRoundPlus },
      { name: 'Private List', labelId: 'privateList', href: '/saved-lists', icon: ListChecks },
    ],
  },
  {
    title: 'KNOWLEDGE SHARING',
    labelId: 'knowledgeSharingSection',
    items: [
      { name: 'View Posts', labelId: 'viewPosts', href: '/viewPost', icon: FileText },
      { name: 'Create Post', labelId: 'createPost', href: '/CreatePost', icon: Plus },
    ],
  },
  {
    title: 'AVAILABILITY',
    labelId: 'availabilitySection',
    items: [
      { name: 'My Availability', labelId: 'availability', href: '/IT-AvailabilityType', icon: Clock },
    ],
  },
  {
    title: 'MANAGER CENTER',
    labelId: 'managerCenterSection',
    items: [
      { name: 'Availability Approvals', labelId: 'availabilityApprovals', href: '/availability-approvals', icon: ClipboardCheck, badge: 'Manager' },
      { name: 'Skill Approvals', labelId: 'skillApprovals', href: '/skill-approvals', icon: ListChecks, badge: 'Manager' },
      { name: 'Workforce Intelligence', labelId: 'workforceIntelligence', href: '/workforce-intelligence', icon: BarChart3, badge: 'Manager' },
      { name: 'Reports', labelId: 'reports', href: '/reports', icon: BarChart3, badge: 'Admin' },
    ],
  },
  {
    title: 'INFORMATION',
    labelId: 'informationSection',
    items: [
      { name: 'About', labelId: 'about', href: '/about', icon: BadgeInfo },
      { name: 'My Feedback', labelId: 'myFeedback', href: '/my-feedback', icon: MessageCircleHeart },
      { name: 'Wishlist', labelId: 'wishlist', href: '/wishlist', icon: Rocket },
    ],
  },
];

const MANAGER_CENTER_SECTION_TITLE = 'MANAGER CENTER';

interface SidebarProps {
  readonly sidebarOpen: boolean;
  readonly collapsed: boolean;
  readonly onClose: () => void;
  readonly onToggleCollapse: () => void;
  readonly onNavigate: (href: string) => void;
}

interface SidebarHeaderProps {
  readonly collapsed: boolean;
  readonly theme: any;
  readonly onClose: () => void;
}

function SidebarHeader({ collapsed, theme, onClose }: SidebarHeaderProps) {
  return (
    <div className="flex items-center justify-between h-16 px-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
      <div className={`flex items-center gap-3 min-w-0 ${collapsed ? 'lg:justify-center lg:w-full' : ''}`}>
        <img src={logo} alt="ISentryx" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
        {!collapsed && (
          <h2 className="hidden sm:block text-xl font-bold truncate" style={{ color: theme.primary }}>
            ISentryx
          </h2>
        )}
      </div>
      <button
        onClick={onClose}
        className="lg:hidden p-2 rounded-md transition-colors"
        style={{ color: theme.text }}
        aria-label="Close sidebar"
      >
        <X className="w-6 h-6" />
      </button>
    </div>
  );
}

export default function Sidebar({ sidebarOpen, collapsed, onClose, onToggleCollapse, onNavigate }: SidebarProps) {
  const location = useLocation();
  const { theme } = useTheme();
  const labels = useMenuLabels();
  const hasManagerCenterAccess = useAppSelector((s) => s.managerCenterAccess.hasAccess);
  const isProfileIncomplete = useAppSelector((s) => s.userProfile.isProfileIncomplete);

  // Manager Center is only visible once the backend confirms the logged-in user has access
  // (via the workforce-intelligence/dashboard check) — see fetchManagerCenterAccess in App.tsx.
  const visibleSidebarSections = useMemo(
    () =>
      sidebarSections.filter(
        (section) => !section.hidden && (section.title !== MANAGER_CENTER_SECTION_TITLE || hasManagerCenterAccess),
      ),
    [hasManagerCenterAccess],
  );

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(visibleSidebarSections.map((section) => [section.title, false])),
  );

  const isItemActive = (item: SidebarItem) => {
    if (item.href === '/groups') {
      return location.pathname === item.href || location.pathname.startsWith('/groups/');
    }
    if (item.href === '/saved-lists') {
      return location.pathname === item.href || location.pathname.startsWith('/saved-lists/');
    }
    return location.pathname === item.href;
  };

  const activeSectionTitle = useMemo(
    () => visibleSidebarSections.find((section) => section.items.some(isItemActive))?.title,
    [location.pathname],
  );

  useEffect(() => {
    if (!activeSectionTitle) return;
    setExpandedSections((current) => ({ ...current, [activeSectionTitle]: true }));
  }, [activeSectionTitle]);

  const handleClick = (href: string) => {
    onNavigate(href);
    onClose();
  };

  const toggleSection = (sectionTitle: string) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionTitle]: !current[sectionTitle],
    }));
  };

  const expandAll = () => {
    setExpandedSections((current) =>
      Object.fromEntries(Object.keys(current).map((key) => [key, true])),
    );
  };

  const collapseAll = () => {
    setExpandedSections((current) =>
      Object.fromEntries(Object.keys(current).map((key) => [key, false])),
    );
  };

  const renderItem = (item: SidebarItem, sectionLabel?: string) => {
    const Icon = item.icon;
    const itemLabel = labels[item.labelId] || item.name;
    const isActive = isItemActive(item);
    const accessibleLabel = sectionLabel ? `${sectionLabel}: ${itemLabel}` : itemLabel;
    const disabledTitle = 'Complete your profile to unlock this';

    return (
      <button
        key={item.name}
        type="button"
        onClick={() => !isProfileIncomplete && handleClick(item.href)}
        disabled={isProfileIncomplete}
        className={`w-full flex items-center ${collapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-lg transition-colors text-left ${
          isActive && !isProfileIncomplete ? 'shadow-sm' : ''
        } ${isProfileIncomplete ? 'opacity-40 cursor-not-allowed' : ''}`}
        style={{
          backgroundColor: isActive && !isProfileIncomplete ? theme.primary : 'transparent',
          color: isActive && !isProfileIncomplete ? theme.contrastText : theme.text,
        }}
        title={isProfileIncomplete ? disabledTitle : (collapsed ? accessibleLabel : undefined)}
        aria-label={accessibleLabel}
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={isProfileIncomplete}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {!collapsed && (
          <span className="ml-3 flex min-w-0 flex-1 items-center justify-between gap-2">
            <span className="truncate">{itemLabel}</span>
            {item.badge ? (
              <span
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{
                  backgroundColor: isActive ? 'rgba(255,255,255,0.18)' : `${theme.primary}14`,
                  color: isActive ? theme.contrastText : theme.primary,
                }}
              >
                {item.badge}
              </span>
            ) : null}
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 flex flex-col h-screen w-64 overflow-visible bg-white shadow-lg transform transition-all duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 ${collapsed ? 'lg:w-16' : 'lg:w-64'}`}
      style={{ backgroundColor: theme.sidebar, borderRight: `1px solid ${theme.border}`, height: '100vh' }}
    >
      <SidebarHeader collapsed={collapsed} theme={theme} onClose={onClose} />
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex absolute top-1/2 -right-3.5 z-[60] h-7 w-7 items-center justify-center rounded-full border bg-white shadow-md transition-colors hover:bg-slate-50"
        style={{ borderColor: theme.border, color: theme.text }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-2 custom-scrollbar">
          {renderItem(dashboardItem)}

          {!collapsed && (
            <div className="flex items-center gap-2 pt-2 pb-1">
              <button
                type="button"
                onClick={expandAll}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors hover:bg-black/5"
                style={{ color: theme.textSecondary || theme.text }}
                title="Expand all sections"
              >
                <ChevronsDown className="h-3.5 w-3.5" />
                <span>Expand All</span>
              </button>
              <button
                type="button"
                onClick={collapseAll}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors hover:bg-black/5"
                style={{ color: theme.textSecondary || theme.text }}
                title="Collapse all sections"
              >
                <ChevronsUp className="h-3.5 w-3.5" />
                <span>Collapse All</span>
              </button>
            </div>
          )}

          {visibleSidebarSections.map((section) => {
            const isExpanded = expandedSections[section.title] ?? false;
            const isActiveSection = section.title === activeSectionTitle;
            const sectionLabel = labels[section.labelId] || section.title;
            return (
              <div key={section.title} className="pt-2">
                {!collapsed ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.title)}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors hover:bg-black/5"
                    style={{ color: isActiveSection ? theme.primary : theme.textSecondary || theme.text }}
                    aria-expanded={isExpanded}
                  >
                    <span className="truncate">{sectionLabel}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                ) : (
                  <div
                    role="separator"
                    aria-label={sectionLabel}
                    className="mx-auto my-2 h-px w-8"
                    style={{ backgroundColor: theme.border }}
                  />
                )}
                <div
                  className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                    isExpanded || collapsed ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="mt-1 space-y-1">
                      {section.items.map((item) => renderItem(item, sectionLabel))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
